import React, { Component } from 'react';

import loadable from '@loadable/component';
import Stats from 'frontend_stats';
import { captureException } from 'error_tracker';

import ApiRequest from 'core/api/request';
import OverlaySpinner from 'core/ui/OverlaySpinner';
import getQueryParam from 'core/utils/getQueryParam';
import { isEmpty } from 'core/utils/objectUtils';

const BannerContent = loadable(() => import(
	/* webpackChunkName: "BannerContent" */
	'./BannerContent'
));
const ReactivateSubModal = loadable(() => import(
	/* webpackChunkName: "ReactivateSubModal" */
	'../ReactivateSubscriptionModal/ReactivateSubscriptionModal'
), {
	fallback: <OverlaySpinner />
});
const ConfirmationModal = loadable(() => import(
	/* webpackChunkName: "ReactivateSubModal" */
	'../ReactivateSubscriptionModal/ConfirmationModal'
));

const bannerViews = {
	'banner': BannerContent,
	'modal': ReactivateSubModal,
	'confirmation': ConfirmationModal
};

const nextViewMap = {
	banner: 'modal',
	modal: 'confirmation'
};

export default class BannerManager extends Component {
	constructor(props) {
		super(props);

		this.state = {
			curView: 'banner',
			bannerId: '',
			bannerContent: '',
			bannerCallToActionType: '',
			giftRecipientFirstName: ''
		};
	}

	componentDidMount() {
		this.syncNotificationsFromServer();
	}

	displayNotification(newNotifications) {
		if (newNotifications.length > 0) {
			this.setState({
				bannerId: newNotifications[newNotifications.length - 1].bannerId,
				bannerContent: newNotifications[newNotifications.length - 1].bannerContent,
				bannerCallToActionType: newNotifications[newNotifications.length - 1].bannerCallToActionType,
				curView: 'banner'
			});
		}
	}

	resetNotification(newNotifications) {
		this.setState({
			bannerId: '',
			bannerContent: '',
			curView: 'banner'
		}, function () {
			setTimeout(() => {
				this.displayNotification(newNotifications);
			}, 2000);
		});
	}

	syncNotificationsFromServer() {
		// prevent in_app server request for marketplace guests or cratejoy admins
		if (window.cj && window.cj.user && (isEmpty(window.cj.user) || window.cj.user.access_level)) {
			return;
		}
		// # now_ts is used to adjust for time sensitive gift notification queries in automation tests
		let now_ts = getQueryParam('now_ts');
		let params = now_ts ? { now_ts } : {};

		ApiRequest.get('/in_app_notifications/', params)
			.then(({ results }) => {
				this.syncNotificationsInStorage(results, this.getNotificationsFromStorage("dismissedNotifications"));
				this.displayNotification(this.getNotificationsFromStorage("newNotifications"));
			})
			.catch(captureException);
	}

	syncNotificationsInStorage(response, dismissedNotifications) {
		let newNotifications = [];

		response.forEach((result) => {
			// Check if response is a gift notifications
			if (result.callToActionType === "reactivate_expired_gift" || result.callToActionType === "extend_not_expired_gift") {
				// Check if the gift notifications exists in dismissedNotifications
				if (dismissedNotifications && dismissedNotifications[result.bannerId]) {
					// Check if the gift notification was dismissed more than 3 days ago
					if ((new Date().getTime() - dismissedNotifications[result.bannerId]) > 259200000) {
						delete dismissedNotifications[result.bannerId];
					}
				} else {
					newNotifications.push({
						bannerId: result.bannerId.toString(),
						bannerContent: result.bannerContent,
						bannerCallToActionType: result.callToActionType
					});
				}
			}
		});

		// Add notifications to their respective storage units
		this.addNotificationstoStorageUnit(dismissedNotifications, "dismissedNotifications");
		this.addNotificationstoStorageUnit(newNotifications, "newNotifications");

		// Clean out any dismissed notifications that are older than 30 days
		this.cleanOldDismissedNotificationsFromStorage(dismissedNotifications);

	}

	getNotificationsFromStorage(storageUnit) {
		return JSON.parse(localStorage.getItem(storageUnit)) || {};
	}

	addNotificationstoStorageUnit(notifications, storageUnit) {
		localStorage.setItem(storageUnit, JSON.stringify(notifications));
	}

	cleanOldDismissedNotificationsFromStorage(dismissedNotifications) {
		// clear out any notifications that were dismissed > 30 days ago
		let dnKeys = Object.keys(dismissedNotifications);
		for (let i = 0; i < dnKeys.length; i += 1) {
			let key = dnKeys[i];
			let value = dismissedNotifications[key];
			if ((new Date().getTime() - value) > 2592000000) {
				delete dismissedNotifications[key];
			}
		}
		this.addNotificationstoStorageUnit(dismissedNotifications, "dismissedNotifications");
	}

	addNotificationtoDismissedStorage() {
		let dismissedNotifications = this.getNotificationsFromStorage("dismissedNotifications");
		let newNotifications = this.getNotificationsFromStorage("newNotifications");

		// remove notification from newNotifications storage
		newNotifications.pop();
		this.addNotificationstoStorageUnit(newNotifications, "newNotifications");

		// add notification to dismissedNotifications storage
		dismissedNotifications[this.state.bannerId] = new Date().getTime();
		this.addNotificationstoStorageUnit(dismissedNotifications, "dismissedNotifications");

		this.resetNotification(newNotifications);
	}

	handleRemoveNotification = () => {
		this.addNotificationtoDismissedStorage();

		Stats.trackGeneric(this.state.bannerCallToActionType, {
			step_name: 'removed_banner',
			subscription_id: this.state.bannerId
		});
	}

	handleRemoveModal = () => {
		this.addNotificationtoDismissedStorage();
	}

	onNext = (nextState = {}) => {
		let { curView } = this.state;
		this.setState({
			...nextState,
			curView: nextViewMap[curView],
		});
	}

	render() {
		const {
			curView,
			bannerId,
			bannerContent,
			bannerCallToActionType,
			giftRecipientFirstName,
		} = this.state;

		if (curView === 'banner' && !bannerContent) {
			// shortcut here before any other components get involved
			return null;
		}

		const View = bannerViews[curView];
		const bannerInfo = {
			bannerId,
			bannerContent,
			bannerCallToActionType,
			giftRecipientFirstName,
		};

		return (
			<View
				{...bannerInfo}
				onDone={this.onNext}
				onRemoveNotif={this.handleRemoveNotification}
				onRemoveModal={this.handleRemoveModal}
				isGift={true}
			/>
		);
	}
}
